import * as React from 'react';
import {
	AppBar,
	Toolbar,
	Typography,
	Box,
	IconButton,
	Tabs,
	Tab,
	Button,
	List,
	ListItemButton,
	ListItemText,
	Divider,
	Paper,
	Chip,
	Tooltip,
	TextField,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Snackbar,
	Alert,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Stack,
	Badge,
	Menu,
	MenuItem,
	Breadcrumbs,
	Link as MLink,
	Switch,
} from '@mui/material';
import {
	Menu as MenuIcon,
	Save as SaveIcon,
	PlayArrow as PlayIcon,
	Bolt as BoltIcon,
	Edit as EditIcon,
	ExpandMore as ExpandMoreIcon,
	IntegrationInstructions as IntegrationIcon,
	Event as EventIcon,
	Schema as SchemaIcon,
	AutoAwesome as AutoIcon,
	Search as SearchIcon,
	MoreVert as MoreIcon,
} from '@mui/icons-material';
import { PanelGroup, Panel, PanelResizeHandle } from 'react-resizable-panels';

/**
 * Operations Workbench
 *
 * One unified workspace for:
 *  - Editing an operation (center)
 *  - Seeing domain objects, external services, and events (right)
 *  - Navigating contexts, operations, and history (left)
 *  - Sending natural-language instructions that either:
 *      a) Update the CURRENT OPERATION, or
 *      b) Propose changes to DOMAIN LOGIC (aggregate roots, methods, invariants)
 *
 * This file is intentionally self-contained and uses mock data/state.
 */
export default function OperationsWorkbench() {
	// Mock data
	const contexts = [
		{
			id: 'customer',
			name: 'Customer Context',
			operations: [
				{ id: 'CreateCustomer', status: 'stable' },
				{ id: 'UpdateCustomerName', status: 'draft' },
				{ id: 'DeactivateCustomer', status: 'stable' },
			],
			domain: {
				aggregates: [
					{
						name: 'Customer',
						methods: ['Create', 'UpdateName', 'Deactivate'],
						invariants: ['Email unique', 'Name not empty'],
					},
				],
			},
			services: [
				{ name: 'EmailService', actions: ['SendWelcomeEmail', 'SendChangeNotice'] },
				{ name: 'CrmGateway', actions: ['PushContact'] },
			],
			events: ['CustomerCreated', 'CustomerNameUpdated', 'CustomerDeactivated'],
		},
	];

	const [activeContext] = React.useState(contexts[0]);
	const [selectedOp, setSelectedOp] = React.useState(activeContext.operations[1]);
	const [centerTab, setCenterTab] = React.useState(0); // 0 Code, 1 Flow, 2 Tests
	const [opCode, setOpCode] = React.useState(
		`// Operation: UpdateCustomerName\nexport async function UpdateCustomerName(request, { repos, services, publish }) {\n  const { customerId, newName } = request;\n  const customer = await repos.customers.get(customerId);\n  customer.updateName(newName);\n  await repos.customers.save(customer);\n  await services.EmailService.SendChangeNotice(customer.email);\n  await publish('CustomerNameUpdated', { id: customerId, newName });\n  return { ok: true };\n}`
	);

	// Instruction console state
	const [instruction, setInstruction] = React.useState('');
	const [diffOpen, setDiffOpen] = React.useState(false);
	const [domainProposalOpen, setDomainProposalOpen] = React.useState(false);
	const [snack, setSnack] = React.useState<{ open: boolean; msg: string; sev: 'success' | 'info' | 'error' }>({ open: false, msg: '', sev: 'success' });

	// Menus
	const [opMenuAnchor, setOpMenuAnchor] = React.useState<null | HTMLElement>(null);

	// Handlers
	const runInstructionToOperation = () => {
		if (!instruction.trim()) {
			setSnack({ open: true, msg: 'Type an instruction first.', sev: 'error' });
			return;
		}
		// Simulate: open diff modal showing changes to the operation code
		setDiffOpen(true);
	};

	const runInstructionToDomain = () => {
		if (!instruction.trim()) {
			setSnack({ open: true, msg: 'Type an instruction first.', sev: 'error' });
			return;
		}
		// Simulate: open domain change proposal
		setDomainProposalOpen(true);
	};

	const applyOperationDiff = () => {
		setDiffOpen(false);
		setSnack({ open: true, msg: 'Operation updated from instruction.', sev: 'success' });
	};

	const applyDomainChanges = () => {
		setDomainProposalOpen(false);
		setSnack({ open: true, msg: 'Domain changes proposed & queued.', sev: 'info' });
	};

	const codeHeader = (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderBottom: 1, borderColor: 'divider' }}>
			<Breadcrumbs>
				<MLink underline="hover" color="inherit">
					{activeContext.name}
				</MLink>
				<Typography color="text.primary">{selectedOp.id}</Typography>
			</Breadcrumbs>
			<Chip size="small" label={selectedOp.status} color={selectedOp.status === 'draft' ? 'warning' : 'default'} />
			<Box sx={{ flex: 1 }} />
			<Tooltip title="Validate & Save">
				<Button variant="outlined" startIcon={<SaveIcon />}>
					Save
				</Button>
			</Tooltip>
			<Tooltip title="Run dry test">
				<Button variant="contained" startIcon={<PlayIcon />}>
					Dry Run
				</Button>
			</Tooltip>
			<IconButton onClick={(e) => setOpMenuAnchor(e.currentTarget)}>
				<MoreIcon />
			</IconButton>
			<Menu anchorEl={opMenuAnchor} open={Boolean(opMenuAnchor)} onClose={() => setOpMenuAnchor(null)}>
				<MenuItem>View History</MenuItem>
				<MenuItem>Open Logs</MenuItem>
				<MenuItem>Delete Operation</MenuItem>
			</Menu>
		</Box>
	);

	return (
		<Box sx={{ height: '100dvh', display: 'grid', gridTemplateRows: 'auto 1fr auto' }}>
			{/* App Bar */}
			<AppBar position="static" elevation={0} color="default">
				<Toolbar>
					<IconButton edge="start">
						<MenuIcon />
					</IconButton>
					<Typography variant="h6" sx={{ flex: 1 }}>
						Operations Workbench
					</Typography>
					<Tooltip title="Global search (⌘K)">
						<IconButton>
							<SearchIcon />
						</IconButton>
					</Tooltip>
					<Tooltip title="Automations">
						<IconButton>
							<BoltIcon />
						</IconButton>
					</Tooltip>
				</Toolbar>
			</AppBar>

			{/* Main Panels */}
			<PanelGroup direction="horizontal" style={{ height: '100%' }}>
				{/* Left: Navigator */}
				<Panel defaultSize={22} minSize={16} maxSize={40}>
					<Box sx={{ height: '100%', display: 'grid', gridTemplateRows: 'auto 1fr', borderRight: 1, borderColor: 'divider' }}>
						<Box sx={{ p: 1.5 }}>
							<Typography variant="subtitle2" color="text.secondary">
								Navigator
							</Typography>
						</Box>
						<Box sx={{ overflow: 'auto', p: 1 }}>
							<Accordion defaultExpanded>
								<AccordionSummary expandIcon={<ExpandMoreIcon />}>
									<Stack direction="row" spacing={1} alignItems="center">
										<BoltIcon fontSize="small" />
										<Typography>Operations</Typography>
										<Badge color="primary" badgeContent={activeContext.operations.length} />
									</Stack>
								</AccordionSummary>
								<AccordionDetails>
									<List dense>
										{activeContext.operations.map((op) => (
											<ListItemButton key={op.id} selected={op.id === selectedOp.id} onClick={() => setSelectedOp(op)}>
												<ListItemText primary={op.id} secondary={op.status} />
											</ListItemButton>
										))}
									</List>
								</AccordionDetails>
							</Accordion>

							<Accordion defaultExpanded>
								<AccordionSummary expandIcon={<ExpandMoreIcon />}>
									<Stack direction="row" spacing={1} alignItems="center">
										<SchemaIcon fontSize="small" />
										<Typography>Domain (Aggregates)</Typography>
									</Stack>
								</AccordionSummary>
								<AccordionDetails>
									<List dense>
										{activeContext.domain.aggregates.map((a) => (
											<ListItemButton key={a.name}>
												<ListItemText primary={a.name} secondary={`Methods: ${a.methods.join(', ')}`} />
											</ListItemButton>
										))}
									</List>
								</AccordionDetails>
							</Accordion>

							<Accordion defaultExpanded>
								<AccordionSummary expandIcon={<ExpandMoreIcon />}>
									<Stack direction="row" spacing={1} alignItems="center">
										<IntegrationIcon fontSize="small" />
										<Typography>External Services</Typography>
									</Stack>
								</AccordionSummary>
								<AccordionDetails>
									<List dense>
										{activeContext.services.map((s) => (
											<ListItemButton key={s.name}>
												<ListItemText primary={s.name} secondary={`Actions: ${s.actions.join(', ')}`} />
											</ListItemButton>
										))}
									</List>
								</AccordionDetails>
							</Accordion>

							<Accordion defaultExpanded>
								<AccordionSummary expandIcon={<ExpandMoreIcon />}>
									<Stack direction="row" spacing={1} alignItems="center">
										<EventIcon fontSize="small" />
										<Typography>Events</Typography>
									</Stack>
								</AccordionSummary>
								<AccordionDetails>
									<List dense>
										{activeContext.events.map((e) => (
											<ListItemButton key={e}>
												<ListItemText primary={e} />
											</ListItemButton>
										))}
									</List>
								</AccordionDetails>
							</Accordion>
						</Box>
					</Box>
				</Panel>

				<PanelResizeHandle className="w-1 bg-gray-200 hover:bg-gray-300 cursor-col-resize" />

				{/* Center: Operation Editor */}
				<Panel defaultSize={50} minSize={30}>
					<Box sx={{ height: '100%', display: 'grid', gridTemplateRows: 'auto auto 1fr' }}>
						{codeHeader}
						<Tabs value={centerTab} onChange={(_, v) => setCenterTab(v)}>
							<Tab label="Code" />
							<Tab label="Flow" />
							<Tab label="Tests" />
						</Tabs>
						<Box sx={{ p: 1, overflow: 'auto' }}>
							{centerTab === 0 && (
								<TextField
									value={opCode}
									onChange={(e) => setOpCode(e.target.value)}
									multiline
									minRows={20}
									fullWidth
									InputProps={{ sx: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13 } }}
								/>
							)}
							{centerTab === 1 && (
								<Paper variant="outlined" sx={{ p: 2 }}>
									<Typography variant="body2" color="text.secondary">
										Flow preview (mock): request → load aggregate → mutate → save → call EmailService → publish CustomerNameUpdated
									</Typography>
								</Paper>
							)}
							{centerTab === 2 && (
								<Box sx={{ display: 'grid', gap: 1 }}>
									<Paper variant="outlined" sx={{ p: 2 }}>
										<Stack direction="row" alignItems="center" spacing={2}>
											<Typography sx={{ flex: 1 }}>Test: should update name and publish event</Typography>
											<Switch />
											<Button size="small" variant="outlined">
												Run
											</Button>
										</Stack>
									</Paper>
									<Paper variant="outlined" sx={{ p: 2 }}>
										<Typography variant="body2" color="text.secondary">
											Add assertions here…
										</Typography>
									</Paper>
								</Box>
							)}
						</Box>
					</Box>
				</Panel>

				<PanelResizeHandle className="w-1 bg-gray-200 hover:bg-gray-300 cursor-col-resize" />

				{/* Right: Inspector & Resources */}
				<Panel defaultSize={28} minSize={22} maxSize={50}>
					<Box sx={{ height: '100%', display: 'grid', gridTemplateRows: 'auto 1fr', borderLeft: 1, borderColor: 'divider' }}>
						<Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
							<Typography variant="subtitle2" color="text.secondary">
								Inspector
							</Typography>
							<Chip size="small" label="Context-aware" />
						</Box>
						<Box sx={{ overflow: 'auto', p: 1, display: 'grid', gap: 2 }}>
							<ResourceCard title="Domain Object" icon={<SchemaIcon fontSize="small" />}>
								<KeyValue name="Aggregate" value="Customer" />
								<KeyValue name="Methods" value="Create, UpdateName, Deactivate" />
								<KeyValue name="Invariants" value="Email unique; Name not empty" />
								<Divider sx={{ my: 1 }} />
								<Button size="small" startIcon={<EditIcon />}>
									Open Domain Editor
								</Button>
							</ResourceCard>

							<ResourceCard title="External Services" icon={<IntegrationIcon fontSize="small" />}>
								<List dense>
									{activeContext.services.map((s) => (
										<ListItemButton key={s.name}>
											<ListItemText primary={s.name} secondary={`Actions: ${s.actions.join(', ')}`} />
										</ListItemButton>
									))}
								</List>
							</ResourceCard>

							<ResourceCard title="Events" icon={<EventIcon fontSize="small" />}>
								<List dense>
									{activeContext.events.map((e) => (
										<ListItemButton key={e}>
											<ListItemText primary={e} />
										</ListItemButton>
									))}
								</List>
							</ResourceCard>
						</Box>
					</Box>
				</Panel>
			</PanelGroup>

			{/* Instruction Console */}
			<Box sx={{ borderTop: 1, borderColor: 'divider', p: 1, display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 1, alignItems: 'center' }}>
				<TextField
					placeholder="Describe the change… e.g., 'Validate newName min length 3 and trim before save; publish CustomerNameUpdated only if changed'"
					value={instruction}
					onChange={(e) => setInstruction(e.target.value)}
					size="small"
					fullWidth
					InputProps={{ startAdornment: <AutoIcon sx={{ mr: 1 }} /> }}
				/>
				<Tooltip title="Apply instruction to the active operation only">
					<Button variant="contained" onClick={runInstructionToOperation}>
						Update Operation
					</Button>
				</Tooltip>
				<Tooltip title="Propose changes to domain objects / invariants">
					<Button variant="outlined" onClick={runInstructionToDomain}>
						Propose Domain Change
					</Button>
				</Tooltip>
			</Box>

			{/* Diff Modal (Operation) */}
			<Dialog open={diffOpen} onClose={() => setDiffOpen(false)} fullWidth maxWidth="md">
				<DialogTitle>Proposed changes to {selectedOp.id}</DialogTitle>
				<DialogContent dividers>
					<Paper variant="outlined" sx={{ p: 2, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13 }}>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
							Diff (mock)
						</Typography>
						<pre
							style={{ margin: 0 }}
						>{`- await publish('CustomerNameUpdated', { id: customerId, newName });\n+ if (customer.nameChanged) {\n+   await publish('CustomerNameUpdated', { id: customerId, newName });\n+ }`}</pre>
					</Paper>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDiffOpen(false)}>Cancel</Button>
					<Button variant="contained" startIcon={<SaveIcon />} onClick={applyOperationDiff}>
						Apply
					</Button>
				</DialogActions>
			</Dialog>

			{/* Domain Proposal Modal */}
			<Dialog open={domainProposalOpen} onClose={() => setDomainProposalOpen(false)} fullWidth maxWidth="sm">
				<DialogTitle>Domain change proposal</DialogTitle>
				<DialogContent dividers>
					<Typography variant="subtitle2" sx={{ mb: 1 }}>
						Aggregate: Customer
					</Typography>
					<List dense>
						<ListItemButton>
							<ListItemText primary="Add method: HasNameChanged(old, next)" secondary="Returns boolean; used by UpdateCustomerName operation" />
						</ListItemButton>
						<ListItemButton>
							<ListItemText primary="Invariant: Name length ≥ 3 after trim" secondary="Validate on UpdateName()" />
						</ListItemButton>
					</List>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDomainProposalOpen(false)}>Cancel</Button>
					<Button variant="contained" onClick={applyDomainChanges}>
						Queue Changes
					</Button>
				</DialogActions>
			</Dialog>

			<Snackbar open={snack.open} autoHideDuration={2500} onClose={() => setSnack({ ...snack, open: false })}>
				<Alert severity={snack.sev} onClose={() => setSnack({ ...snack, open: false })}>
					{snack.msg}
				</Alert>
			</Snackbar>
		</Box>
	);
}

function ResourceCard({ title, icon, children }: { title: string; icon?: React.ReactNode; children: React.ReactNode }) {
	return (
		<Paper variant="outlined" sx={{ p: 1.5 }}>
			<Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
				{icon}
				<Typography variant="subtitle2">{title}</Typography>
			</Stack>
			{children}
		</Paper>
	);
}

function KeyValue({ name, value }: { name: string; value: React.ReactNode }) {
	return (
		<Box sx={{ display: 'grid', gridTemplateColumns: '140px 1fr', alignItems: 'baseline', gap: 1, py: 0.5 }}>
			<Typography variant="caption" color="text.secondary">
				{name}
			</Typography>
			<Typography variant="body2">{value}</Typography>
		</Box>
	);
}
